import { useCallback, useReducer, useRef } from 'react';
import { t } from '@lingui/macro';

import { apiSearchByNameRetrieve } from '@rover/rsdk/src/apiClient/latest/api-search-by-name-retrieve';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { SearchNameQuerySubmittedEvent, SearchNameResultsRenderedEvent } from '../analyticsEvents';
import { mapToCardResult } from '../api/searchByNameAdapter';
import type { NameSearchResult, SearchByNameAction, SearchByNameState } from '../types';
import {
  getQueryNumberInSession,
  incrementQueryNumberInSession,
} from '../utils/queryNumberInSession';

const initialState: SearchByNameState = {
  query: '',
  results: null,
  searchUuid: null,
  isLoading: false,
  error: null,
};

// Localized at call-time so Lingui picks up the active locale; do not hoist.
const getGenericSearchError = (): string => t`Something went wrong. Please try again.`;

function reducer(state: SearchByNameState, action: SearchByNameAction): SearchByNameState {
  switch (action.type) {
    case 'SEARCH_START':
      return {
        ...state,
        query: action.query,
        searchUuid: null,
        isLoading: true,
        error: null,
      };
    case 'SEARCH_SUCCESS':
      return {
        ...state,
        results: action.results,
        searchUuid: action.searchUuid,
        isLoading: false,
      };
    case 'SEARCH_ERROR':
      return { ...state, error: action.error, isLoading: false };
    default:
      return state;
  }
}

// lat/lng are required: the search-by-name backend rejects requests without
// either a bbox or center+zoom. The screen pre-resolves coords (autocomplete
// pick, seeded geocode, or submit-time geocode) before calling search().
export type SearchParams = {
  name: string;
  lat: string;
  lng: string;
};

type UseSearchByNameReturn = {
  state: SearchByNameState;
  search: (params: SearchParams) => Promise<void>;
};

export default function useSearchByName(): UseSearchByNameReturn {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Drop out-of-order responses when searches overlap.
  const requestIdRef = useRef(0);
  // Capture per-search start time so the results-rendered effect can compute latency.
  const searchStartRef = useRef<number>(0);

  const search = useCallback(async ({ name, lat, lng }: SearchParams): Promise<void> => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const startedAt = Date.now();
    searchStartRef.current = startedAt;

    const queryNumberInSession = incrementQueryNumberInSession();
    const wordCount = name.trim().split(/\s+/).filter(Boolean).length;
    emitAnalyticsEvent(
      new SearchNameQuerySubmittedEvent({
        wordCount,
        length: name.length,
        queryNumberInSession,
      })
    );

    dispatch({ type: 'SEARCH_START', query: name });

    try {
      const response = await apiSearchByNameRetrieve({
        nameQuery: name,
        centerlat: Number(lat),
        centerlng: Number(lng),
      });
      if (requestIdRef.current !== requestId) return;

      const results: NameSearchResult[] = (response.results ?? []).map(mapToCardResult);
      const searchUuid: string | null = response.searchUuid ?? null;

      dispatch({
        type: 'SEARCH_SUCCESS',
        results,
        searchUuid,
      });

      emitAnalyticsEvent(
        new SearchNameResultsRenderedEvent({
          resultCount: results.length,
          isZeroResults: results.length === 0,
          searchUuid,
          latencyMs: Date.now() - startedAt,
        })
      );
    } catch {
      if (requestIdRef.current !== requestId) return;
      dispatch({ type: 'SEARCH_ERROR', error: getGenericSearchError() });
    }
  }, []);

  return { state, search };
}

// Re-export so callers can read the current session counter without depending on internals.
export { getQueryNumberInSession };
